import React, { useState, useMemo } from 'react';
import { Map, Grid } from 'lucide-react';
import HeroSection from '../components/HeroSection';
import SearchFilters from '../components/SearchFilters';
import MapView from '../components/MapView';
import TerrainCard from '../components/TerrainCard';
import AgencySection from '../components/AgencySection';
import { terrainListings } from '../mockData';
import { Button } from '../components/ui/button';
import { haversineDistance } from '../lib/utils';
import AnimatedSection from '../components/AnimatedSection';

const TerrainsPage = () => {
  const [filters, setFilters] = useState({
    location: '',
    radius: 0,
    maxBudget: null,
    minSurface: null,
    maxSurface: null
  });
  const [viewMode, setViewMode] = useState('both'); // 'map', 'list', 'both'

  const filteredTerrains = useMemo(() => {
    const normalizedLocation = filters.location.trim().toLowerCase();
    const radiusCenter = normalizedLocation
      ? terrainListings.find(
          (terrain) => terrain.location.toLowerCase() === normalizedLocation
        )?.coordinates
      : null;

    return terrainListings.filter((terrain) => {
      const terrainLocation = terrain.location.toLowerCase();

      // Проверяем location, commune или hameau
      const searchInLocation = normalizedLocation && (
        terrainLocation.includes(normalizedLocation) ||
        (terrain.commune && terrain.commune.toLowerCase().includes(normalizedLocation)) ||
        (terrain.hameau && terrain.hameau.toLowerCase().includes(normalizedLocation))
      );
      
      if (normalizedLocation && !searchInLocation) {
        return false;
      }

      if (filters.radius > 0 && radiusCenter) {
        const distance = haversineDistance(
          [terrain.coordinates.lat, terrain.coordinates.lng],
          [radiusCenter.lat, radiusCenter.lng]
        );

        if (distance > filters.radius) {
          return false;
        }
      }

      // Если цена не указана (0), пропускаем фильтр по бюджету
      if (filters.maxBudget && terrain.price > 0 && terrain.price > filters.maxBudget) {
        return false;
      }

      // Если поверхность не указана (0), пропускаем фильтр по поверхности
      if (filters.minSurface && terrain.surface > 0 && terrain.surface < filters.minSurface) {
        return false;
      }

      if (filters.maxSurface && terrain.surface > 0 && terrain.surface > filters.maxSurface) {
        return false;
      }

      return true;
    });
  }, [filters]);

  return (
    <div className="min-h-screen bg-white">
      <HeroSection />

      {/* Search Section with Map */}
      <div className="bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* View Toggle Buttons */}
          <div className="flex justify-center mb-6 space-x-2">
            <Button
              variant={viewMode === 'map' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('map')}
              className={`transition-all duration-200 ${viewMode === 'map' ? 'bg-[#c4d600] hover:bg-[#b0c200] text-gray-800 shadow-md' : 'hover:border-[#c4d600]'}`}
            >
              <Map className="w-4 h-4 mr-2" />
              Carte
            </Button>
            <Button
              variant={viewMode === 'both' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('both')}
              className={`transition-all duration-200 ${viewMode === 'both' ? 'bg-[#c4d600] hover:bg-[#b0c200] text-gray-800 shadow-md' : 'hover:border-[#c4d600]'}`}
            >
              <Grid className="w-4 h-4 mr-2" />
              Liste et carte
            </Button>
          </div>

          {/* Map and Filters Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Map */}
            {(viewMode === 'map' || viewMode === 'both') && (
              <div className="lg:col-span-2">
                <MapView terrains={filteredTerrains} />
              </div>
            )}

            {/* Filters */}
            <div className={viewMode === 'list' ? 'lg:col-span-3' : ''}>
              <SearchFilters
                onFilterChange={setFilters}
                showMap={viewMode !== 'list'}
                onToggleView={setViewMode}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="bg-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-800 text-center mb-8 animate-fadeInUp">
            {filteredTerrains.length === 0 && 'Aucun terrain ne correspond à votre recherche'}
            {filteredTerrains.length === 1 && '1 terrain correspond à votre recherche'}
            {filteredTerrains.length > 1 && `${filteredTerrains.length} terrains correspondent à votre recherche`}
          </h2>

          {/* Terrain Cards Grid */}
          {filteredTerrains.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTerrains.map((terrain, index) => (
                <AnimatedSection 
                  key={terrain.id} 
                  animation="fade-up" 
                  delay={index * 50}
                  className="h-full"
                >
                  <TerrainCard terrain={terrain} />
                </AnimatedSection>
              ))}
            </div>
          )}

          {/* Expand Search Button */}
          {filteredTerrains.length > 0 && (
            <div className="text-center mt-12">
              <Button
                variant="outline"
                className="text-gray-700 hover:text-[#c4d600] border-gray-300"
              >
                Élargir votre zone de recherche
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Agencies Section */}
      <AgencySection />
    </div>
  );
};

export default TerrainsPage;